import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import BackDrop from "../../../components/ui/BackDrop";
import { cn } from "../../../utils/twMerge";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { datePickerIcon, closeIcon } from "../../../assetData/icons";
import CustomDateInput from "../../../components/ui/custom-date-input/CustomDateInput";
import MirageLoader from "../../../components/ui/pageLoader/MirageLoader";

const MasterCouponEdit = ({
  buttonConfig,
  selectedItemId,
  isButtonClicked,
}) => {
  const getOneHandler = buttonConfig.getOneHandler;
  const editHandler = buttonConfig.editHandler;
  const closeHandler = buttonConfig.closeEditModal;

  const [handleValue, handleSetValue] = useState("");

  const schema = z.object({
    description: z.string().nonempty(),
    coupon_code: z.string().nonempty(),
    unit: z.string(),
    coupon_type: z.string(),
    value: z.string(),
    eff_start_date: z.string(),
    eff_end_date: z.string(),
  });

  const form = useForm({
    defaultValues: async () => {
      const data = await getOneHandler(selectedItemId);

      return {
        description: data.description,
        coupon_code: data.coupon_code,
        unit: data.unit,
        coupon_type: data.coupon_type,
        value: data.value,
        eff_start_date: data.eff_start_date,
        eff_end_date: data.eff_end_date,
      };
    },
    resolver: zodResolver(schema),
  });

  const errors = form.formState.errors;

  const onSubmit = async (data) => {
    const updatedData = { ...data };

    const res = await editHandler(selectedItemId, updatedData);
    if (res) closeHandler();
  };

  const onClose = () => {
    closeHandler();
  };

  return (
    <>
      <div className="fixed left-1/2 z-50 mx-auto min-h-[300px] w-[98%] max-w-[800px] -translate-x-1/2 rounded bg-white p-8 max-sm:top-[10%] sm:top-1/2 sm:-translate-y-1/2">
        <h2 className="pb-4 text-2xl font-bold text-neutral-800">
          Edit Coupon
        </h2>

        <button
          type="button"
          className="absolute right-8 top-8"
          onClick={onClose}
        >
          <img src={closeIcon} />
        </button>

        <form onSubmit={form.handleSubmit(onSubmit)}>
          {/* row 1 */}
          <div className="gap-2 sm:flex ">
            <div className={cn("flex w-full flex-col  gap-2")}>
              <label className="text-base text-[#636363]">Description</label>
              <input
                placeholder="Type here"
                {...form.register("description")}
                className={cn(
                  "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                  errors.description && "border-[1px] border-red-300",
                )}
              />
            </div>
            <div className={cn("flex w-full flex-col gap-2  max-sm:mt-3")}>
              <label className="text-base text-[#636363]">Coupon Code</label>
              <input
                placeholder="Type here"
                {...form.register("coupon_code")}
                className={cn(
                  "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                  errors.coupon_code && "border-[1px] border-red-300",
                )}
              />
            </div>
            <div
              className={cn("flex w-full flex-col gap-2 max-md:hidden")}
            ></div>{" "}
          </div>

          {/* row 2 */}
          <div className="gap-2 max-md:mt-3 max-md:grid max-md:grid-cols-2 max-sm:grid-cols-1 md:mt-8 md:flex">
            <div className={cn("flex w-full flex-col  gap-2")}>
              <label className="text-base text-[#636363]">Unit</label>
              <select
                {...form.register("unit")}
                className={cn(
                  "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                  errors.unit && "border-[1px] border-red-300",
                )}
              >
                <option value="">Select</option>
                <option value="single">Single</option>
                <option value="bulk">Bulk</option>
              </select>
            </div>
            <div className={cn("flex w-full flex-col gap-2  max-sm:mt-3")}>
              <label className="text-base text-[#636363]">Type</label>

              <select
                {...form.register("coupon_type")}
                className={cn(
                  "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                  errors.coupon_type && "border-[1px] border-red-300",
                )}
              >
                <option value="">Select</option>
                <option value="percent">Percent</option>
                <option value="fixed">Fixed</option>
              </select>
            </div>

            <div className={cn("flex w-full flex-col gap-2  max-sm:mt-3")}>
              <label className="text-base text-[#636363]">Value</label>
              <input
                placeholder="Type here"
                {...form.register("value")}
                className={cn(
                  "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                  errors.value && "border-[1px] border-red-300",
                )}
              />
            </div>
          </div>
          <div className=" gap-2 max-md:mt-3 sm:flex md:mt-8">
            <div className={cn("flex w-full flex-col  gap-2")}>
              <label className="text-base text-[#636363]">Eff Start Date</label>
              <CustomDateInput
                handleSetValue={handleSetValue}
                initialValue={form.getValues("eff_start_date")}
                form1={form.register("eff_start_date")}
                className="w-full rounded-[4px] bg-neutral-100"
              />
            </div>

            <div className={cn("flex w-full flex-col gap-2  max-sm:mt-3")}>
              <label className="text-base text-[#636363]">Eff End Date</label>
              <CustomDateInput
                form1={form.register("eff_end_date")}
                handleSetValue={handleSetValue}
                handleValue={handleValue}
                initialValue={form.getValues("eff_end_date")}
                min={form.getValues("eff_start_date")}
                className="w-full rounded-[4px] bg-neutral-100"
              />
            </div>
            <div
              className={cn("flex w-full flex-col gap-2 max-md:hidden")}
            ></div>
          </div>

          <div className="flex items-end justify-between gap-2 max-md:[&>button]:w-1/2 max-sm:[&>button]:w-full md:[&>button]:w-1/3">
            <button
              type="submit"
              disabled={isButtonClicked}
              className={` ${
                isButtonClicked ? "opacity-[70%]" : ""
              } mt-8 w-full rounded border-2 border-pri-clr-orange bg-pri-clr-orange px-16 py-2 font-semibold text-white `}
            >
              {isButtonClicked ? <MirageLoader /> : "SAVE CHANGES"}
            </button>
          </div>
        </form>
      </div>
      <BackDrop />
    </>
  );
};

export default MasterCouponEdit;
